/**
 * Bridge between the API client and the "Confirm it's you" prompt.
 *
 * Big account actions (billing, team members, sales-channel connections) answer 403
 * `reauth_required` unless the person confirmed their identity in the last 10 minutes
 * (backend middleware/step-up.ts). The axios interceptor in api.ts calls `requestReauth`, the
 * <ReauthPrompt> mounted in App asks for the password / emailed code / 2FA code, and on success
 * the original request is retried — so call sites need no changes.
 */
export interface ReauthRequest {
  /** The account has 2FA on, so the prompt must also ask for the authenticator code. */
  mfaRequired: boolean
}

type ReauthHandler = (request: ReauthRequest) => Promise<boolean>

let handler: ReauthHandler | null = null
let pending: Promise<boolean> | null = null

/** Called by <ReauthPrompt> on mount (and with null on unmount). */
export function registerReauthHandler(next: ReauthHandler | null): void {
  handler = next
}

/**
 * Ask the person to confirm it's them. Resolves true once confirmed, false if they cancel or
 * no prompt is mounted. Requests that hit the gate together share one prompt.
 */
export function requestReauth(request: ReauthRequest): Promise<boolean> {
  if (!handler) return Promise.resolve(false)
  if (!pending) {
    pending = handler(request).finally(() => {
      pending = null
    })
  }
  return pending
}

/** True when an API error body is the step-up gate's refusal. */
export function isReauthRequired(data: unknown): data is { code: 'reauth_required'; mfaRequired?: boolean } {
  return !!data && typeof data === 'object' && (data as { code?: unknown }).code === 'reauth_required'
}
